import {useCallback, useMemo} from 'react';
import {Resolver, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router';
import {yupResolver} from '@hookform/resolvers/yup';
import {useStoreContext} from '@store/store-context';
import {activateNotification} from '@ui/molecules/notification/activate-notification';
import {beamMasteryTasks} from '@utils/firebase-request';
import {CreateFeedbackValidator} from '../create-feedback/create-feedback-validator';
import {FeedbackController} from '../feedback-controller';
import {FeedbackRequestImpl} from '../feedback-requests';

interface RequestFeedbackFormData {
  user: string;
  subjectName: string;
}

const useRequestFeedback = ({
  onSuccess,
}: {
  onSuccess?: () => void | Promise<void>;
} = {}) => {
  const {
    usersStore: {users, deactivatedUsers},
    authStore: {auth},
  } = useStoreContext();
  const navigate = useNavigate();
  const {
    handleSubmit,
    errors,
    formState,
    setValue,
    trigger,
    control,
    watch,
  } = useForm<RequestFeedbackFormData>({
    resolver: yupResolver(CreateFeedbackValidator.requestFeedback) as Resolver<
      RequestFeedbackFormData,
      object
    >,
    mode: 'all',
    defaultValues: {
      user: '',
      subjectName: '',
    },
  });

  const handleFormValueChange = useCallback(
    async (value: string, name: keyof RequestFeedbackFormData) => {
      setValue(name, value);
      if (formState.submitCount > 0) await trigger();
    },
    [formState.submitCount, setValue, trigger],
  );
  const {user, subjectName} = watch();
  const feedbackRequest = useMemo(() => new FeedbackRequestImpl(), []);
  const feedbackController = useMemo(
    () => new FeedbackController(feedbackRequest),
    [feedbackRequest],
  );

  const onSubmit = async (data: RequestFeedbackFormData) => {
    try {
      const response = await feedbackController.postRequestFeedback(
        {
          subjectType: 'custom',
          subjectName: data.subjectName,
        },
        data.user,
      );

      if (auth.user.role === 'user' && !auth.user.isReviewer) {
        response && beamMasteryTasks('request_feedback', true);
      }
      response &&
        activateNotification({
          content: 'Your request has been submitted.',
          title: 'Success',
          kind: 'success',
        });
      if (response) {
        if (onSuccess) await onSuccess();
        else navigate(-1);
      }
    } catch (error) {}
  };

  const deactivatedUserIds = new Set(
    deactivatedUsers.map((member: any) => member.id),
  );
  const userOptions = users
    .filter(
      (member: any) =>
        member.id !== auth.user.id && !deactivatedUserIds.has(member.id),
    )
    .map((member: any) => {
      const name = member.firstName
        ? `${member.firstName} ${member.lastName}`
        : `${member.email} (pending invitation)`;

      return {
        label: {
          avatar: {
            name,
            src: member.avatar?.url || '',
          },
          id: member.id,
          name,
        },
        value: member.id,
      };
    });

  return {
    handleFormValueChange,
    handleSubmit,
    users: userOptions,
    errors,
    control,
    onSubmit,
    isDisabled: !user || !subjectName,
    isSubmitting: formState.isSubmitting,
  };
};

export default useRequestFeedback;
